import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import { IconButton, ListItem } from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLInputField, GraphQLInputObjectType } from 'graphql';
import React from 'react';

import { useAnalyticsContext } from 'src/app/graph/explorerPage/hooks/useAnalyticsContext';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  GraphQLArgumentNode,
  unwrapType,
} from 'src/lib/graphql/graphqlHelpers';

import { FieldToggle } from '../fieldToggle/FieldToggle';
import { FieldToggleType } from '../getFieldToggle/getFieldToggle';

export const InputField = ({
  argumentPath = [],
  inputField,
  onSelect,
  toggleNodeInOperation,
  isNodeInOperation,
  disableToggle,
  shouldManageVariables,
}: {
  argumentPath?: GraphQLArgumentNode[];
  inputField: GraphQLInputField;
  onSelect?: (field: GraphQLArgumentNode) => void;
  toggleNodeInOperation?: NonNullable<FieldToggleType>['toggleArgumentPathInOperation'];
  isNodeInOperation?: NonNullable<FieldToggleType>['isArgumentPathValueSet'];
  shouldManageVariables: boolean;
  disableToggle?: boolean;
}) => {
  const { track } = useAnalyticsContext();
  const defaultValue: string | undefined = JSON.stringify(
    inputField.defaultValue,
  );

  return (
    <ListItem
      className={classnames(
        // pl-10 accounts for checkbox button that's absolutely positioned
        toggleNodeInOperation && isNodeInOperation && 'pl-10',
        'group relative flex flex-1 items-center gap-2 font-mono text-sm',
      )}
      title={`${inputField.name}: ${inputField.type.toString()}${
        defaultValue ? ` = ${defaultValue}` : ''
      }`}
      onClick={() => onSelect?.(inputField)}
    >
      {toggleNodeInOperation && isNodeInOperation && (
        <Tooltip
          label={
            shouldManageVariables
              ? "Inlined arguments currently can't be modified via UI toggle."
              : "Argument fields can't be toggled via UI in embedded view or when variable management is disabled."
          }
          disabled={!disableToggle}
        >
          <IconButton
            // these button overrides are because our "hidden" variant doesn't work well
            // in Explorer where its bg is the same as Explorer's bg
            className="absolute left-1 size-7 min-w-0 bg-transparent hover:bg-btn-secondary"
            aria-label="Toggle field to be checked or unchecked"
            variant="hidden"
            size="sm"
            isDisabled={disableToggle}
            onClick={(event) => {
              event.stopPropagation();
              const wasIncluded = isNodeInOperation([
                ...argumentPath,
                inputField,
              ]);
              track(wasIncluded ? `Toggle Argument Off` : `Toggle Argument On`);
              if (
                !wasIncluded &&
                onSelect &&
                unwrapType(inputField) instanceof GraphQLInputObjectType
              ) {
                onSelect(inputField);
              }

              toggleNodeInOperation([...argumentPath, inputField]);
            }}
            icon={
              disableToggle ? (
                <IconUnavailable className="group-hover:text-secondary" />
              ) : (
                <FieldToggle
                  checked={
                    isNodeInOperation([...argumentPath, inputField])
                      ? 'checked'
                      : 'unchecked'
                  }
                />
              )
            }
          />
        </Tooltip>
      )}
      <span
        className={classnames(
          'flex items-center truncate text-primary',
          inputField.deprecationReason && 'line-through',
        )}
      >
        {inputField.name}:
      </span>
      <span className="flex-1 truncate text-code-f">
        {inputField.type.toString()}
        {defaultValue && <> = {defaultValue}</>}
      </span>
      {inputField.deprecationReason && (
        <Tooltip label={`Deprecated: ${inputField.deprecationReason}`}>
          <span className="flex cursor-pointer items-center pl-2">
            <IconWarningSolid />
          </span>
        </Tooltip>
      )}
      <IconArrowRight className="ml-1 hidden group-hover:block" />
    </ListItem>
  );
};
